import React, { useEffect, useState } from "react";
import "./AddSessionForm.css";
import PrimaryButton from "../../../Atoms/Buttons/Primary/PrimaryButton";
import useSave from "../../../../Hooks/useSave";
import { Formik } from "formik";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import TextArea from "../../../Atoms/Inputs/TextArea/TextArea";
import * as Yup from "yup";
import TextFieldInput from "../../../Atoms/Inputs/TextField/TextFieldInput";
import { useNavigate } from "react-router-dom";
import { MenuItem, Typography } from "@mui/material";
import Button from "@mui/material/Button";
import { BaseURL, classesURL, courseURL } from "../../../../Hooks/URL";
import useFetchWhen from "../../../../Hooks/useFetchWhen";
import dayjs from "dayjs";
import { PulseLoader } from "react-spinners";
import useEdit from "../../../../Hooks/useEdit";
import FileAttachmentInput from "../../../Molecules/FileAttachmentInput/FileAttachmentInput";

const sessionSchema = Yup.object().shape({
  title: Yup.string().required("Title required !!"),
  content: Yup.string().required("Content required !!"),
  lessonId: Yup.string().required("Lesson required !!"),
});

function AddSessionForm({ Edit = false, url, Class, session = null }) {
  const navigate = useNavigate();
  const [newObject, setNewObject] = useState({});
  const [sessionDate, setSessionDate] = useState(dayjs(new Date()) ?? "");
  const [existingAttachments, setExistingAttachments] = useState(
    session?.task?.attachments?.map((file, index) => ({
      id: file?.id,
      file: file?.filePath,
      name: file?.name,
      size: 0,
      type: "application/octet-stream",
      isExisting: true,
    })) ?? []
  );
  const [newAttachments, setNewAttachments] = useState([]);
  const [deletedAttachmentIds, setDeletedAttachmentIds] = useState([]);
  const [goSave, setGoSave] = useState(false);
  const [goEdit, setGoEdit] = useState(false);
  const { saveState, loading: addLoading } = useSave({
    url: `${classesURL}/${Class?.id}/sessions`,
    goSave,
    setGoSave,
    newObject,
    isImage: true,
  });
  const { state, loading: editLoading } = useEdit(
    `${classesURL}/${Class?.id}/sessions/${session?.id}`,
    goEdit,
    setGoEdit,
    newObject,
    null,
    true
  );

  const { data: lessonsList } = useFetchWhen(
    `${courseURL}/${Class?.course?.id}/lessons`,
    Class?.course?.id
  );

  // Initialize attachments when Class data changes (for edit mode)
  // Initialize attachments when course data changes (for edit mode)
  useEffect(() => {
    if (session && Edit) {
      setExistingAttachments(
        session?.task?.attachments?.map((file, index) => ({
          id: file?.id,
          file: file?.filePath,
          name: file?.name,
          size: 0,
          type: "application/octet-stream",
          isExisting: true,
        })) ?? []
      );
      setNewAttachments([]);
      setDeletedAttachmentIds([]);
    }
  }, [session, Edit]);

  useEffect(() => {
    if (state.dataRes.status == "success") {
      navigate(`/classes/${Class?.id}/sessions/${session?.id}`);
    }
  }, [state]);

  useEffect(() => {
    if (saveState.dataRes.status == "success") {
      navigate(`/classes/${Class?.id}/sessions`);
    }
  }, [saveState]);

  return (
    <div className="courseSmallFormWrapper">
      <Formik
        validationSchema={sessionSchema}
        initialValues={{
          title: session?.title ?? "",
          content: session?.content ?? "",
          lessonId: session?.lesson?.id ?? "",
          taskDescription: session?.task?.description ?? "",
        }}
        onSubmit={(values) => {
          if (Edit) {
            // For edit mode, use the new API structure
            const newAttachmentsFiles = newAttachments.map((att) => att.file);
            const newAttachmentsNames = newAttachments.map((att) => att.name);

            const apiBody = {
              title: values.title,
              content: values.content,
              lessonId: values.lessonId,
              taskDescription: values.taskDescription,
              attachmentsFiles: newAttachmentsFiles,
              deletedAttachmentsIds: deletedAttachmentIds,
              attachmentsNames: newAttachmentsNames,
              dateTime: sessionDate.add(1, "day"),
            };

            setNewObject(apiBody);
            setGoEdit(true);
          } else {
            // For create mode, use the original structure
            const allAttachments = [...existingAttachments, ...newAttachments];
            const attachmentsFiles = allAttachments.map((att) => att.file);
            const attachmentsNames = allAttachments.map((att) => att.name);

            setNewObject({
              ...values,
              dateTime: sessionDate.add(1, "day"),
              attachmentsFiles: attachmentsFiles,
              attachmentsNames: attachmentsNames,
            });
            setGoSave(true);
          }
        }}
      >
        {({
          values,
          errors,
          touched,
          handleChange,
          handleBlur,
          handleSubmit,
        }) => (
          <form autoComplete="off" onSubmit={handleSubmit}>
            <div className="ClassSessionFormContainer">
              <div className="LeftForm">
                <div style={{ width: "100%" }}>
                  <label className="aboutLabel" style={{ marginBottom: "3px" }}>
                    Session Lesson:
                  </label>
                  <TextFieldInput
                    select
                    newValue={values.lessonId}
                    name={"lessonId"}
                    change={handleChange}
                    blur={handleBlur}
                    fullWidth
                    className={`Input`}
                    variant="outlined"
                    SelectProps={{
                      displayEmpty: true,
                    }}
                  >
                    <MenuItem value="" className="teacherSelect" disabled>
                      Choose Lesson
                    </MenuItem>
                    {lessonsList?.data?.map((courseItem) => (
                      <MenuItem value={courseItem?.id}>
                        Lesson {courseItem?.number} - {courseItem?.title}
                      </MenuItem>
                    ))}
                  </TextFieldInput>
                  {errors.lessonId && touched.lessonId && errors.lessonId && (
                    <p className="error">
                      {errors.lessonId && touched.lessonId && errors.lessonId}
                    </p>
                  )}
                </div>

                <div style={{ width: "100%" }}>
                  <label className="aboutLabel" style={{ marginBottom: "3px" }}>
                    Session Title:
                  </label>
                  <TextFieldInput
                    fullWidth
                    type="text"
                    placeholder={"Enter Session Title"}
                    className="Input"
                    // label={"Program Name"}
                    name={"title"}
                    change={handleChange}
                    blur={handleBlur}
                    newValue={values.title}
                  />
                  {errors.title && touched.title && errors.title && (
                    <p className="error">
                      {errors.title && touched.title && errors.title}
                    </p>
                  )}
                </div>

                <div style={{ width: "100%" }}>
                  <label className="aboutLabel">Session Content:</label>
                  <TextArea
                    style={{ marginBottom: "20px" }}
                    classes="textAreaInput"
                    label={"Content"}
                    name={"content"}
                    change={handleChange}
                    blur={handleBlur}
                    newValue={values.content}
                  />
                  {errors.content && touched.content && errors.content && (
                    <p className="error">
                      {errors.content && touched.content && errors.content}
                    </p>
                  )}
                </div>

                <label className="aboutLabel">Session Date:</label>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <DemoContainer components={["DatePicker"]}>
                    <DatePicker
                      className="DataInput"
                      name={"sessionDate"}
                      value={sessionDate}
                      onChange={(newValue) => setSessionDate(newValue)}
                      fullWidth
                    />
                  </DemoContainer>
                </LocalizationProvider>

                <label className="aboutLabel" style={{ paddingTop: "20px" }}>
                  Task Description:
                </label>
                <TextArea
                  classes="textAreaInputLarge"
                  name={"taskDescription"}
                  change={handleChange}
                  blur={handleBlur}
                  newValue={values.taskDescription}
                />
                {errors.taskDescription &&
                  touched.taskDescription &&
                  errors.taskDescription && (
                    <p className="errorTop">
                      {errors.taskDescription &&
                        touched.taskDescription &&
                        errors.taskDescription}
                    </p>
                  )}

                {/* Attachments Section */}
                <FileAttachmentInput
                  existingAttachments={existingAttachments}
                  newAttachments={newAttachments}
                  deletedAttachmentIds={deletedAttachmentIds}
                  onAttachmentUpload={(files) => {
                    const newFiles = files.map((file) => ({
                      file: file,
                      name: file.name,
                      size: file.size,
                      type: file.type,
                      isExisting: false,
                    }));
                    setNewAttachments((prev) => [...prev, ...newFiles]);
                  }}
                  onRemoveAttachment={(index, isExisting) => {
                    if (isExisting) {
                      setExistingAttachments((prev) =>
                        prev.filter((_, idx) => idx !== index)
                      );
                    } else {
                      setNewAttachments((prev) =>
                        prev.filter((_, idx) => idx !== index)
                      );
                    }
                  }}
                  onUpdateDeletedIds={setDeletedAttachmentIds}
                  onUpdateExistingAttachments={setExistingAttachments}
                  onUpdateNewAttachments={setNewAttachments}
                  maxFiles={5}
                  label="Task Attachments:"
                  accept="*/*"
                  disabled={false}
                  baseURL={BaseURL}
                />
              </div>

              <div style={{ display: "flex", justifyContent: "center" }}>
                <button
                  className="actionButton primary addButton"
                  onClick={handleSubmit}
                  disabled={Edit ? editLoading : addLoading}
                >
                  {Edit ? (
                    editLoading ? (
                      <PulseLoader color="white" size={8} />
                    ) : (
                      "Edit"
                    )
                  ) : addLoading ? (
                    <PulseLoader color="white" size={8} />
                  ) : (
                    "Add"
                  )}
                </button>
              </div>
            </div>
          </form>
        )}
      </Formik>
    </div>
  );
}

export default AddSessionForm;
